import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

/**
 * Open registration.
 *
 * Signing up does not create an account — it sends a confirmation link, and the
 * account appears when that link is clicked. The copy says so plainly, because
 * "check your email" with no explanation is where most signups are abandoned.
 *
 * The two acknowledgements are not decoration. What keeps this legal to run
 * without a licence is that the credits have no monetary value and no way out,
 * and the person has to have been told that before they start.
 */
export function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 10;
  const ready = name.trim().length >= 2 && email.length > 3 && password.length >= 10 && accepted;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.register({
        email: email.trim(),
        password,
        displayName: name.trim(),
        acceptedTerms: true,
      });
      setSent(true);
      setDevLink(res.devLink ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the exchange.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="card login">
        <h2>Check your email</h2>
        <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>
          We sent a confirmation link to <b>{email}</b>. Click it and you'll be signed in.
        </p>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-3)' }}>
          The link works once and expires in 15 minutes. Your account is created when you use it —
          until then nothing has been saved.
        </p>
        {devLink && (
          <div className="notice ok" style={{ wordBreak: 'break-all' }}>
            <b>Development mode</b> — no mail provider is configured, so here is the link:
            <br />
            <a href={devLink} style={{ color: 'var(--yes)', textDecoration: 'underline' }}>
              {devLink}
            </a>
          </div>
        )}
        <button style={{ marginTop: 16 }} onClick={() => setSent(false)}>
          Use a different address
        </button>
      </div>
    );
  }

  return (
    <div className="card login">
      <h2>Create an account</h2>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', marginTop: -4 }}>
        You start with 1,000.00 play credits and can trade straight away.
      </p>

      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="name">Display name</label>
          <input
            id="name"
            required
            autoComplete="nickname"
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="How you appear on the leaderboard"
          />
        </div>

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="password"
              type={show ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 10 characters"
            />
            <button
              type="button"
              style={{ padding: '5px 10px', fontSize: 13, whiteSpace: 'nowrap' }}
              onClick={() => setShow(!show)}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
          <div className="hint" style={tooShort ? { color: 'var(--bad)' } : undefined}>
            {tooShort
              ? `${10 - password.length} more character${10 - password.length === 1 ? '' : 's'} needed.`
              : 'A few unrelated words you will remember beats a short scramble you will not.'}
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            fontSize: 13.5,
            lineHeight: 1.5,
            color: 'var(--ink-2)',
            cursor: 'pointer',
            marginBottom: 4,
          }}
        >
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            style={{ width: 'auto', marginTop: 3, flex: 'none' }}
          />
          <span>
            I am 18 or over, and I understand that credits here are for play only — they have no
            monetary value, cannot be bought, and cannot be withdrawn or cashed out.
          </span>
        </label>

        <button className="submit" data-side="YES" disabled={!ready || busy}>
          {busy ? 'Sending…' : 'Create account'}
        </button>
        {error && <div className="notice error">{error}</div>}
      </form>

      <p style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 18, marginBottom: 0 }}>
        Already have an account? <Link to="/login" style={{ color: 'var(--yes)' }}>Sign in</Link>.
      </p>
    </div>
  );
}
